import { Col, Form, Row } from 'react-bootstrap'
import { ContactSelectDropdown } from '../MeetingSummary/CRM/ContactDropdown'
import { Controller, useFormContext } from 'react-hook-form'
import { FormFeedback } from '../../components/Form/FormFeedback'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { selectContact, selectIsLoadingContact, selectShowCreateContact, selectShowLinkContact, setExternalId } from '../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../common/integrations'

export type ContactCreateFormSectionProps = {
  serviceId: string
}

export const UpdateExternalContactLinkSection: React.FC<ContactCreateFormSectionProps> = ({ serviceId }) => {
  const dispatch = useAppDispatch()
  const contact = useAppSelector(selectContact)
  const IsLoadingContact = useAppSelector(selectIsLoadingContact)
  const showCreateZocksContact = useAppSelector(selectShowCreateContact)
  const showLinkToExistingZocksContact = useAppSelector(selectShowLinkContact)
  const form = useFormContext()
  const [hasExternalEmail, setHasExternalEmail] = useState<boolean>(true)
  const integrationDisplayName = getIntegrationDisplayName(serviceId as IntegrationServiceId)
  const handleContactSelected = useCallback(
    (option: SelectOption) => {
      dispatch(setExternalId(option.id))
    },
    [dispatch],
  )

  useEffect(() => {
    setHasExternalEmail(!!contact && !!contact.email)
  }, [contact])

  const initialSelectedOptionExternalId = useMemo(() => {
    return contact?.integrations?.find((i) => i.serviceId === serviceId)?.externalId
  }, [contact, serviceId])

  return (
    <>
      <Row className='mt-2 mb-2'>
        <Col>
          <ContactSelectDropdown
            serviceId={serviceId}
            onContactSelected={handleContactSelected}
            initialSelectedOptionExternalId={initialSelectedOptionExternalId}
            isLoading={IsLoadingContact}
          />
        </Col>
      </Row>
      {showCreateZocksContact ? (
        <Row className='mt-2 mb-4'>
          <Col className='d-flex align-items-center zocks-gap-1 align-self-center '>
            <i className='bi bi-info-circle zocks-text-gray-900' />
            <span className='zocks-text-gray-900'> A new Zocks contact will be created and linked to the selected {integrationDisplayName} contact. </span>
          </Col>
        </Row>
      ) : null}

      <>
        {contact && !hasExternalEmail ? (
          <>
            <Row className='mt-2 mb-4'>
              <Col className='d-flex align-items-center zocks-gap-1 align-self-center '>
                <span className='zocks-text-gray-900'>
                  {' '}
                  This contact doesn't have an email address in {integrationDisplayName}. Add one to create the Zocks contact.{' '}
                </span>
              </Col>
            </Row>
            <Row className='mb-4'>
              <Col>
                <Controller
                  name='email'
                  control={form.control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Enter a valid email address',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <Form.Label>Email</Form.Label>
                      <Form.Control id={field.name} type='text' placeholder='Email…' {...field} isInvalid={!!fieldState.error} />
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </>
                  )}
                />
              </Col>
            </Row>
          </>
        ) : null}
      </>
      {showLinkToExistingZocksContact ? (
        <Row className='mt-2 mb-4'>
          <Col>
            <Controller
              name='linkToExistingZocksContact'
              control={form.control}
              render={({ field }) => {
                return <Form.Check checked={field.value} onChange={field.onChange} type='checkbox' label='Link to existing Zocks contact' />
              }}
            />
          </Col>
        </Row>
      ) : null}
    </>
  )
}
